// WebSocket Service for Real-time Updates
class WebSocketService {
    constructor() {
        this.ws = null;
        this.listeners = {};
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.reconnectTimer = null;
        this.manualDisconnect = false;
        this.realtimeUnavailable = false;
        this.connectPromise = null;
    }

    connect(userId, isAdmin = true) {
        this.manualDisconnect = false;
        if (this.realtimeUnavailable) {
            // Real-time updates are optional: every page still refreshes
            // through its API request. Do not keep retrying a proxy that
            // cannot forward WebSocket upgrades.
            return Promise.resolve(false);
        }
        if (this.ws?.readyState === WebSocket.OPEN) return Promise.resolve(true);
        // The dashboard layout and the dashboard page both subscribe during
        // startup. They must share one attempt, not create two handshakes.
        if (this.connectPromise) return this.connectPromise;

        // Use secure WebSocket in production, regular in development.
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const host = process.env.REACT_APP_WS_HOST || (isLocal ? '127.0.0.1:5002' : `${window.location.host}`);
        const url = `${protocol}//${host}/ws?userId=${userId}&isAdmin=${isAdmin}`;
        const isSameOrigin = host === window.location.host;

        this.connectPromise = (async () => {
            // A WebSocket endpoint returns 400/426 to a normal HTTP request.
            // A 200 HTML response means nginx has fallen through to the SPA,
            // so attempting `new WebSocket()` would only create noisy errors.
            if (!isLocal && isSameOrigin) {
                try {
                    const httpUrl = url.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
                    const probe = await fetch(httpUrl, { method: 'HEAD', cache: 'no-store' });
                    const contentType = probe.headers.get('content-type') || '';
                    if (probe.status === 200 && /text\/html/i.test(contentType)) {
                        this.realtimeUnavailable = true;
                        console.info('Real-time updates are unavailable until the /ws proxy is configured.');
                        return false;
                    }
                } catch (error) {
                    // A failed HTTP probe must not block a valid WebSocket on
                    // a proxy that intentionally rejects non-upgrade requests.
                }
            }
            if (this.manualDisconnect) return false;

            return new Promise((resolve, reject) => {
                this.ws = new WebSocket(url);

                this.ws.onopen = () => {
                    console.log('✅ WebSocket connected');
                    this.reconnectAttempts = 0;
                    this.emit('connected');
                    resolve(true);
                };

                this.ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        this.emit(data.type, data.payload);
                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                    }
                };

                this.ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    this.emit('error', error);
                    this.realtimeUnavailable = true;
                    reject(error);
                };

                this.ws.onclose = () => {
                    console.log('🔌 WebSocket disconnected');
                    this.emit('disconnected');
                    if (!this.manualDisconnect && !this.realtimeUnavailable) {
                        this.attemptReconnect();
                    }
                };
            });
        })();

        return this.connectPromise.finally(() => {
            this.connectPromise = null;
        });
    }

    attemptReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const userId = localStorage.getItem('adminSession')
                ? JSON.parse(localStorage.getItem('adminSession')).userId
                : null;
            if (userId) {
                this.reconnectTimer = setTimeout(() => {
                    console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                    // A reconnect is best-effort.  The initial callers handle
                    // their own rejection, but this background attempt must
                    // never become an uncaught promise in the browser.
                    this.connect(userId).catch(() => {});
                }, this.reconnectDelay * this.reconnectAttempts);
            }
        }
    }

    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
    }

    off(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
        }
    }

    emit(event, data) {
        if (this.listeners[event]) {
            this.listeners[event].forEach(callback => callback(data));
        }
    }

    send(type, payload) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type, payload }));
        } else {
            console.warn('WebSocket is not connected');
        }
    }

    disconnect() {
        this.manualDisconnect = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
}

const wsService = new WebSocketService();
export default wsService;
